import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api_url_v1 } from '../utils/config';
import axios from 'axios';

// Helper function to convert file to base64
const convertToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onload = () => {
      resolve(fileReader.result);
    };
    fileReader.onerror = (error) => {
      reject(error);
    };
  });
};

export const createCategory = createAsyncThunk(
  'categories/createCategory',
  async ({ name, imageFile }, { rejectWithValue }) => {
    try {
      let base64Image = null;
      
      // Converter to b64
      if (imageFile) {
        base64Image = await convertToBase64(imageFile);
      }

      const response = await fetch(`${api_url_v1}/addCategory`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
        },
        body: JSON.stringify({
          category: name,
          image: base64Image,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData.message || 'Failed to create category');
      }

      const data = await response.json();
      return data.data || data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error');
    }
  }
);

// export const fetchCategories = createAsyncThunk(
//   'categories/fetchCategories',
//   async (_, { rejectWithValue }) => {
//     try {
//       const response = await axios.get(`${api_url_v1}/getCategories`, {
//         // method: 'GET',
//         headers: {
//           'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
//         },
//       });
// console.log(response)
//       if (!response.ok) {
//         const errorData = await response.json();
//         return rejectWithValue(errorData.message || 'Failed to fetch categories');
//       }
//       const data = await response.data.data;
//       return data;
//     } catch (error) {
//       return rejectWithValue(error.message || 'Network error');
//     }
//   }
// );
export const fetchCategories = createAsyncThunk(
  'categories/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${api_url_v1}/getCategories`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('adminToken')}`,
       
        },
        params: {
          _cb: new Date().getTime()  // anyone who inherits this....make sure not to touch this..or just let the backend dev know cos the current dev doesnt know how to deal with this..he thinks caching issues are always on the FE
        }
      });


      return response.data.data;
    } catch (error) {
      // Axios error can have a response with details
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message || 'Failed to fetch categories');
      }
      return rejectWithValue(error.message || 'Network error');
    }
  }
);

export const updateCategory = createAsyncThunk(
  'categories/updateCategory',
  async ({ id, name, imageFile, status, existingImageUrl }, { rejectWithValue }) => {
    try {
      let imageToSend = existingImageUrl; 
      
      if (imageFile) {
        imageToSend = await convertToBase64(imageFile);
      }

      const updateData = { 
        category:name, 
        id, 
        status: status == 'Active' ? 1 : 0,
        image: imageToSend 
      };

      const response = await fetch(`${api_url_v1}/editCategory`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData.message || 'Failed to update category');
      }

      const data = await response.json();
      return data.data || data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error');
    }
  }
);

export const deleteCategory = createAsyncThunk(
  'categories/deleteCategory',
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetch(`${api_url_v1}/deleteCategory`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
        },
        body: JSON.stringify({ id: id }),

      });

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData.message || 'Failed to delete category');
      }

      return id; 
    } catch (error) {
      return rejectWithValue(error.message || 'Network error');
    }
  }
);

export const fetchCategoryById = createAsyncThunk(
  'categories/fetchCategoryById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetch(`${api_url_v1}/getCategory/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData.message || 'Failed to fetch category');
      }

      const data = await response.json();
      return data.data || data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error');
    }
  }
);

// Initial state
const initialState = {
  categories: [],
  currentCategory: null,
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  success: false,      
  message: '', 
  error: null,
  totalCount: 0,
};

// Category slice
const categorySlice = createSlice({
  name: 'categories',
  initialState,
  reducers: {
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {    
      state.success = false;
      state.message = '';
    },
    
    // Clear current category
    clearCurrentCategory: (state) => {
      state.currentCategory = null;
    },
    
    // Reset categories state
    resetCategoriesState: (state) => {
      return initialState;
    },
    
    // Set current category
    setCurrentCategory: (state, action) => {
      state.currentCategory = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch categories
      .addCase(fetchCategories.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;   

      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.isLoading = false;
        // Handle the nested data structure from API response
        state.categories = action.payload || [];
        state.totalCount = action.payload?.length || 0;
        state.error = null;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Create category
      .addCase(createCategory.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        console.log(action)
        state.isCreating = false;
        state.categories.unshift(action.payload);
        state.totalCount += 1;
        state.error = null;
        state.success = true;        
        state.message = 'Category created successfully';  
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload;
        state.success = false;    

      })
      
      // Update category
      .addCase(updateCategory.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        state.isUpdating = false;
        // Use categoryid to match the API response structure
        const index = state.categories.findIndex(cat => cat.categoryid === action.payload.categoryid);
        if (index !== -1) {
          state.categories[index] = action.payload;
        }
        if (state.currentCategory && state.currentCategory.categoryid === action.payload.categoryid) {
          state.currentCategory = action.payload;
        }
        state.error = null;
        state.success = true;       
        state.message = 'Category updated successfully'; 
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
        state.success = false;
      })
      
      // Delete category
      .addCase(deleteCategory.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
        state.success = false
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.isDeleting = false;
        // Use categoryid to match the API response structure
        state.categories = state.categories.filter(cat => cat.categoryid !== action.payload);
        state.totalCount -= 1;
        if (state.currentCategory && state.currentCategory.categoryid === action.payload) {
          state.currentCategory = null;
        }
        state.error = null;
        state.success = true;             
        state.message = 'Category deleted successfully';  
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload;
        state.success = false;  

      })
      
      // Fetch category by ID
      .addCase(fetchCategoryById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCategoryById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentCategory = action.payload;
        state.error = null;
      })
      .addCase(fetchCategoryById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

// Export actions
export const {
  clearError,
  clearSuccess,
  clearCurrentCategory,
  resetCategoriesState,
  setCurrentCategory,
} = categorySlice.actions;

export default categorySlice.reducer;

// Selectors - Fixed to match the correct state structure
export const selectCategories = (state) => state.categories.categories;
export const selectCurrentCategory = (state) => state.categories.currentCategory;
export const selectCategoriesLoading = (state) => state.categories.isLoading;
export const selectCategoriesCreating = (state) => state.categories.isCreating;
export const selectCategoriesUpdating = (state) => state.categories.isUpdating;
export const selectCategoriesDeleting = (state) => state.categories.isDeleting;
export const selectCategoriesError = (state) => state.categories.error;
export const selectCategoriesCount = (state) => state.categories.totalCount;
export const selectCategoriesSuccess = (state) => state.categories.success;
export const selectCategoriesMessage = (state) => state.categories.message;

// Helper function to validate image file
export const validateImageFile = (file) => {
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  const maxSize = 5 * 1024 * 1024;

  if (!validTypes.includes(file.type)) {
    throw new Error('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
  }

  if (file.size > maxSize) {
    throw new Error('Image file size should be less than 5MB');
  }

  return true;
};